import { API_BASE } from './apiBase';
// src/NotificationBell.js
// Global notification bell for the SideNav (sits just above Logout).
//
// Two-section model:
//  - ACTIVE  = things that still want you: every UNRESOLVED actionable item
//    (stays pinned even after you click it, until the batch is saved/discarded),
//    plus UNREAD informational items.
//  - HISTORY = collapsible, last 20: resolved actionable items + read
//    informational items. Click "History" to expand/collapse.
//
// Clicking an informational item marks it read -> it slides into History.
// Clicking an actionable item navigates but it stays in Active until resolved.
// "Clear" sweeps only informational items into History (never actionable to-dos).
// The badge counts Active items. Self-contained; safe to remove.
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Bell, AlertTriangle, Upload, RotateCcw, CheckCheck, ChevronRight, ChevronDown, X } from 'lucide-react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';

const API = `${API_BASE}`;

const ICON = {
  upload_complete: Upload,
  review_required: AlertTriangle,
  return_routed: RotateCcw,
  rejected_to_suspense: AlertTriangle,
  review_resolved: CheckCheck,
  info: Bell,
};

// An item belongs in ACTIVE if it's an unresolved action, or unread info.
const isActive = (n) => (n.actionable ? !n.resolved : !n.read);

function timeAgo(d) {
  const t = new Date(d).getTime();
  if (isNaN(t)) return '';
  const s = Math.floor((Date.now() - t) / 1000);
  if (s < 60) return 'just now';
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}

export default function NotificationBell() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const navigate = useNavigate();
  const rootRef = useRef(null);

  const authHeaders = () => {
    const token = localStorage.getItem('token');
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  const load = useCallback(async () => {
    if (!localStorage.getItem('token')) return;
    try {
      const res = await axios.get(`${API}/notifications`, { headers: authHeaders() });
      setItems(res.data.items || []);
    } catch (_) { /* offline / unauthorized — stay quiet */ }
  }, []);

  useEffect(() => {
    load();
    const id = setInterval(load, 8000);
    // Instant refresh when another part of the app changes notifications
    // (e.g. deleting a batch resolves its "needs allocation" alert) so the
    // bell updates immediately instead of waiting for the next 8s poll.
    const onRefresh = () => load();
    window.addEventListener('notifications:refresh', onRefresh);
    return () => { clearInterval(id); window.removeEventListener('notifications:refresh', onRefresh); };
  }, [load]);

  useEffect(() => {
    const onDoc = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  const markRead = async (n) => {
    if (!n.read) {
      try { await axios.post(`${API}/notifications/${n._id}/read`, {}, { headers: authHeaders() }); } catch (_) {}
      setItems(prev => prev.map(x => (x._id === n._id ? { ...x, read: true } : x)));
    }
  };

  // Clear = sweep informational items to History (leave actionable to-dos).
  const clearInformational = async () => {
    try { await axios.post(`${API}/notifications/clear`, {}, { headers: authHeaders() }); } catch (_) {}
    setItems(prev => prev.map(x => (!x.actionable ? { ...x, read: true } : x)));
  };

  // Clear history = permanently delete the history (read/resolved) items. Active untouched.
  const clearHistory = async (e) => {
    e.stopPropagation();
    try { await axios.post(`${API}/notifications/clear-history`, {}, { headers: authHeaders() }); } catch (_) {}
    setItems(prev => prev.filter(isActive));
    setHistoryOpen(false);
  };

  const onClickItem = async (n) => {
    await markRead(n);
    setOpen(false);
    if (n.link) navigate(n.link);
  };

  const active = items.filter(isActive);
  const history = items.filter(n => !isActive(n)).slice(0, 20);
  const badge = active.length;
  const hasClearable = active.some(n => !n.actionable);

  const renderItem = (n, faded) => {
    const Icon = ICON[n.type] || Bell;
    const isAction = n.actionable && !n.resolved;
    return (
      <button
        key={n._id}
        onClick={() => onClickItem(n)}
        className={`w-full text-left flex gap-3 px-4 py-3 border-b border-zinc-800/60 transition-colors hover:bg-zinc-800/40 ${faded ? 'opacity-60' : (n.read ? '' : 'bg-indigo-950/20')}`}
      >
        <span className={`mt-0.5 ${isAction ? 'text-amber-400' : 'text-indigo-400'}`}>
          <Icon className="w-4 h-4" />
        </span>
        <span className="flex-1 min-w-0">
          <span className="flex items-center gap-2">
            <span className="text-[13px] font-medium text-zinc-100 truncate">{n.title}</span>
            {n.project?.poNumber && (
              <span className="text-[10px] font-bold bg-indigo-500/15 text-indigo-300 px-1.5 py-0.5 rounded shrink-0" title={n.project.title || ''}>
                #{n.project.poNumber}
              </span>
            )}
            {n.fromAdmin && (
              <span className="text-[9px] font-bold uppercase tracking-wide bg-fuchsia-500/15 text-fuchsia-300 px-1.5 py-0.5 rounded shrink-0">
                From Admin
              </span>
            )}
            {isAction && <span className="text-[9px] uppercase tracking-wide text-amber-400/80 shrink-0">action</span>}
          </span>
          {n.message && <span className="block text-[11px] text-zinc-400 mt-0.5 leading-snug">{n.message}</span>}
          <span className="block text-[10px] text-zinc-600 mt-1">{timeAgo(n.createdAt)}</span>
        </span>
      </button>
    );
  };

  return (
    <div ref={rootRef} className="relative px-3 pb-2">
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center gap-3 px-4 py-2.5 text-sm font-medium rounded-lg transition-colors text-zinc-400 hover:text-white hover:bg-zinc-800/40"
      >
        <span className="relative">
          <Bell className="w-5 h-5" />
          {badge > 0 && (
            <span className="absolute -top-2 -right-2 min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center">
              {badge > 99 ? '99+' : badge}
            </span>
          )}
        </span>
        Notifications
      </button>

      {open && (
        <>
          {/* mobile: dim the page behind the card */}
          <div className="md:hidden fixed inset-0 bg-black/60 z-[70]" onClick={() => setOpen(false)} />
          {/* desktop: small popover above the bell — unchanged.
              mobile: a real card pinned to the visible viewport with its own X. */}
          <div className="absolute bottom-full left-3 mb-2 w-80 max-h-[65vh] flex flex-col bg-zinc-900 border border-zinc-700 rounded-xl shadow-2xl z-[60] overflow-hidden
            max-md:fixed max-md:inset-x-3 max-md:top-12 max-md:bottom-16 max-md:left-3 max-md:mb-0 max-md:w-auto max-md:max-h-none max-md:z-[80]">
          <div className="flex items-center justify-between px-4 py-2.5 border-b border-zinc-800">
            <span className="text-sm font-semibold text-white">Notifications</span>
            <span className="flex items-center gap-2">
              {hasClearable && (
                <button onClick={clearInformational} className="text-[11px] text-indigo-400 hover:text-indigo-300 inline-flex items-center gap-1">
                  <CheckCheck className="w-3.5 h-3.5" /> Clear
                </button>
              )}
              <button onClick={() => setOpen(false)} title="Close" className="md:hidden p-1.5 -mr-1.5 rounded-lg hover:bg-zinc-800 text-zinc-400">
                <X className="w-4 h-4" />
              </button>
            </span>
          </div>

          <div className="flex-1 overflow-y-auto">
            {active.length === 0 ? (
              <div className="px-4 py-8 text-center text-zinc-500 text-sm">You're all caught up.</div>
            ) : (
              active.map(n => renderItem(n, false))
            )}

            {/* History toggle */}
            {history.length > 0 && (
              <>
                <div className="flex items-center border-t border-zinc-800 bg-zinc-950/40">
                  <button
                    onClick={() => setHistoryOpen(o => !o)}
                    className="flex-1 flex items-center gap-1.5 px-4 py-2 text-[11px] font-medium text-zinc-500 hover:text-zinc-300"
                  >
                    {historyOpen ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
                    History ({history.length})
                  </button>
                  {historyOpen && (
                    <button
                      onClick={clearHistory}
                      className="px-4 py-2 text-[11px] font-medium text-zinc-500 hover:text-red-300"
                    >
                      Clear history
                    </button>
                  )}
                </div>
                {historyOpen && (
                  <div className="max-h-[40vh] overflow-y-auto bg-zinc-950/20">
                    {history.map(n => renderItem(n, true))}
                  </div>
                )}
              </>
            )}
          </div>
          </div>
        </>
      )}
    </div>
  );
}
